import React, {useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import {getCategories} from '../../../services/teamExperienceService'
import {searchByType} from '../../../services/searchService'
import apiClient from '../../../helpers/apiClient'
import {ExperienceCard} from '../../members/components/ExperienceCard'
import {MemberExperienceCategories} from '../../members/components/MemberExperienceCategories'
import {parseResultsSearch} from '../resultsParams'
import {ExperienceSummary} from '../../teamExperiences/models/ExperienceSummaryModel'
import {useInfiniteExperiencesSearch} from '../../../hooks/experiences/useInfiniteExperiencesSearch'

// Maximum upcoming experiences shown under "Other upcoming experiences". Enforced at the
// backend via `?limit=…`; the small client-side buffer swallows any match that also shows up
// in the recency-ordered preview so the section never shrinks below OTHERS_LIMIT.
const OTHERS_LIMIT = 20
const OTHERS_BUFFER = 5

const asRawList = (data: any): any[] => {
  if (Array.isArray(data)) return data
  return Array.isArray(data?.results) ? data.results : []
}

const fetchExperiencesByIds = async (ids: number[]): Promise<any[]> => {
  if (ids.length === 0) return []
  const {data} = await apiClient.get('/experiences/search/', {
    params: {ids: ids.join(','), limit: ids.length},
  })
  return asRawList(data)
}

const fetchExperiencesPreview = async (category?: number): Promise<any[]> => {
  const params: Record<string, string | number> = {
    where: '',
    key: '',
    limit: OTHERS_LIMIT + OTHERS_BUFFER,
  }
  if (category != null) params.category = category
  const {data} = await apiClient.get('/experiences/search/', {params})
  return asRawList(data)
}

// "Vigente" = the experience still has at least one date whose end (or start, when end is
// missing) is in the future. Uses local wall clock — the backend returns ISO datetimes.
const isUpcoming = (exp: ExperienceSummary): boolean => {
  const now = Date.now()
  return (exp.dates ?? []).some((d) => {
    const iso = d?.end || d?.start
    if (!iso) return false
    const t = new Date(iso).getTime()
    return !Number.isNaN(t) && t > now
  })
}

const PER_PAGE = 20

const ExperiencesSearchPage: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  const [categories, setCategories] = useState<Array<{id: string; name: string}>>([])
  const [matches, setMatches] = useState<ExperienceSummary[]>([])
  const [others, setOthers] = useState<ExperienceSummary[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const {q} = parseResultsSearch(location.search)
  const trimmed = (q ?? '').trim()
  const hasQuery = trimmed.length > 0

  // Modo "discovery": sin query, listamos por infinite scroll ordenado por
  // `-created_at` (PR-I-56). Con query, mantenemos el flujo matches + preview
  // "Other upcoming experiences" (paginación cliente, tamaño acotado).
  const infinite = useInfiniteExperiencesSearch({
    enabled: !hasQuery,
    pageSize: PER_PAGE,
    category: selectedCategoryId,
  })

  // Reset a discovery: click en el título "Experiences" limpia la query,
  // idéntico al patrón de /members-search.
  const resetToDiscovery = () => {
    if (location.pathname === '/experiences-search' && !location.search) {
      history.replace('/experiences-search')
    } else {
      history.push('/experiences-search')
    }
  }

  useEffect(() => {
    const container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    return () => container?.classList.remove('member-full-wrapper')
  }, [])

  useEffect(() => {
    ;(async () => {
      const cats = await getCategories()
      setCategories(cats?.results || [])
    })()
  }, [])

  useEffect(() => {
    // Sin query: la carga la maneja el hook de infinite scroll.
    if (!hasQuery) {
      setMatches([])
      setOthers([])
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const category = selectedCategoryId != null ? Number(selectedCategoryId) : undefined

        // 1. Aggregator gives us the ids that match, same as the header dropdown.
        const res = await searchByType('experiences', trimmed, 100, 0)
        const matchIds: number[] = (res.results ?? [])
          .map((r) => Number((r as any)?.id))
          .filter((n) => !Number.isNaN(n))

        // 2. Hidrate matches (?ids=…) and fetch a small preview for "Other upcoming
        // experiences" in parallel. Both payloads are small.
        const [matchesRaw, previewRaw] = await Promise.all([
          fetchExperiencesByIds(matchIds),
          fetchExperiencesPreview(category),
        ])

        const matchesById = new Map(
          (matchesRaw as ExperienceSummary[]).map((x) => [x.id, x])
        )
        const matchesData = matchIds
          .map((id) => matchesById.get(id))
          .filter((x): x is ExperienceSummary => Boolean(x))

        const matchIdSet = new Set(matchesData.map((x) => x.id))
        const upcoming = (previewRaw as ExperienceSummary[])
          .filter((x) => !matchIdSet.has(x.id) && isUpcoming(x))
          .slice(0, OTHERS_LIMIT)

        if (cancelled) return
        setMatches(matchesData)
        setOthers(upcoming)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [trimmed, selectedCategoryId, hasQuery])

  const onChangeCategory = (categoryId?: number) => {
    setSelectedCategoryId(categoryId !== undefined && categoryId !== null ? String(categoryId) : null)
  }

  const hasMatches = matches.length > 0
  const showResultsHeader = hasQuery
  const showOthersHeader = hasQuery && others.length > 0

  const subheadingStyle: React.CSSProperties = {
    fontFamily: "'Oswald', sans-serif",
    fontSize: 20,
    fontWeight: 500,
    color: '#FFFFFF',
    margin: 0,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  }

  return (
    <div className='bg-experiences-section-black'>
      <div className='container-experience-search'>
        <div className='members-page mt-experiences-section-black'>
          <div className='members-page__header'>
            <h1
              className='members-page__title text-white'
              style={{cursor: 'pointer'}}
              role='link'
              tabIndex={0}
              onClick={resetToDiscovery}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  resetToDiscovery()
                }
              }}
            >
              Experiences&nbsp;
              <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
            </h1>
          </div>
        </div>

        <MemberExperienceCategories
          data={categories}
          loading={false}
          onChangeCategory={onChangeCategory}
          selectedId={selectedCategoryId}
          onSelectedIdChange={setSelectedCategoryId}
        />

        {showResultsHeader && (
          <div style={{marginTop: 8, marginBottom: 18}}>
            <h2 style={subheadingStyle}>Results for &ldquo;{trimmed}&rdquo;</h2>
          </div>
        )}

        {hasQuery && hasMatches && (
          <div className='container-experience-search-wrapper-cards'>
            {matches.map((item, idx) => (
              <ExperienceCard experience={item} key={`m-${item.id ?? idx}`} />
            ))}
          </div>
        )}

        {hasQuery && !hasMatches && !loading && (
          <div
            style={{
              padding: '48px 0 8px',
              color: '#B4B4B4',
              fontFamily: 'Satoshi, sans-serif',
              fontSize: 16,
              textAlign: 'center',
            }}
          >
            No experiences match &ldquo;{trimmed}&rdquo;.
          </div>
        )}

        {/* Modo con query: preview "Other upcoming experiences" (tamaño acotado). */}
        {hasQuery && others.length > 0 && (
          <>
            {showOthersHeader && (
              <div style={{marginTop: 40, marginBottom: 18}}>
                <h2 style={subheadingStyle}>Other upcoming experiences</h2>
              </div>
            )}
            <div className='container-experience-search-wrapper-cards'>
              {others.map((item, idx) => (
                <ExperienceCard experience={item} key={`o-${item.id ?? idx}`} />
              ))}
            </div>
          </>
        )}

        {/* Modo discovery (sin query): infinite scroll silencioso — sin botón
            "Load more", el sentinel dispara la siguiente página. */}
        {!hasQuery && (
          <>
            {infinite.items.length > 0 && (
              <div className='container-experience-search-wrapper-cards'>
                {infinite.items.map((item, idx) => (
                  <ExperienceCard experience={item} key={`d-${item.id ?? idx}`} />
                ))}
              </div>
            )}
            <div ref={infinite.sentinelRef} aria-hidden='true' style={{height: 1}} />
          </>
        )}
      </div>
    </div>
  )
}

export {ExperiencesSearchPage}
